import type { BadgeVariant } from '../components/ui/Badge';
import type { DealStage } from '../types/deal';
import type { TaskStatus, TaskPriority } from '../types/task';

// ─── Deal stage ──────────────────────────────────────────────────────────────
export const dealStageVariant = (stage: DealStage): BadgeVariant => {
  switch (stage) {
    case 'Closed Won':                 return 'success';
    case 'Closed Lost':
    case 'Closed Lost to Competition': return 'danger';
    case 'Negotiation/Review':         return 'info';
    case 'Proposal/Price Quote':       return 'success';
    case 'Identify Decision Makers':   return 'purple';
    case 'Value Proposition':          return 'orange';
    case 'Needs Analysis':             return 'warning';
    case 'Qualification':
    default:                           return 'info';
  }
};

// ─── Project / generic status ────────────────────────────────────────────────
export const projectStatusVariant = (status?: string): BadgeVariant => {
  if (!status) return 'default';
  const s = status.toLowerCase();
  if (s.includes('completed') || s.includes('approved') || s.includes('invoiced')) return 'success';
  if (s.includes('in progress') || s.includes('active') || s.includes('on track')) return 'info';
  if (s.includes('delayed') || s.includes('cancelled')) return 'danger';
  if (s.includes('on hold') || s.includes('testing') || s.includes('planning')) return 'warning';
  return 'default';
};

// ─── Case status ─────────────────────────────────────────────────────────────
export const caseStatusVariant = (status: string): BadgeVariant => {
  const map: Record<string, BadgeVariant> = {
    ready:              'info',
    working:            'warning',
    waiting:            'orange',
    reviewing:          'purple',
    approved:           'success',
    uploading:          'indigo',
    closed:             'default',
    additional_request: 'pink',
  };
  return map[status] ?? 'default';
};

// ─── Case priority ───────────────────────────────────────────────────────────
export const casePriorityVariant = (priority: string): BadgeVariant => {
  const map: Record<string, BadgeVariant> = {
    low:    'success',
    medium: 'warning',
    high:   'danger',
  };
  return map[priority] ?? 'default';
};

// ─── Task status ─────────────────────────────────────────────────────────────
export const taskStatusVariant = (status: TaskStatus | string): BadgeVariant => {
  const map: Record<string, BadgeVariant> = {
    'Not Started':      'default',
    'Deferred':         'orange',
    'In Progress':      'info',
    'in_progress':      'info',
    'Completed':        'success',
    'completed':        'success',
    'Waiting for input':'warning',
  };
  return map[status] ?? 'default';
};

// ─── Task priority ───────────────────────────────────────────────────────────
export const taskPriorityVariant = (priority: TaskPriority | string): BadgeVariant => {
  const map: Record<string, BadgeVariant> = {
    Highest: 'danger',
    High:    'orange',
    Normal:  'info',
    Low:     'warning',
    Lowest:  'success',
  };
  return map[priority] ?? 'default';
};

// ─── User status ─────────────────────────────────────────────────────────────
export const userStatusVariant = (status?: string): BadgeVariant =>
  status === 'active' ? 'success' : 'danger';

// ─── Project-request status ───────────────────────────────────────────────────
export const projectRequestStatusVariant = (status: string, hasDeal: boolean): BadgeVariant => {
  if (!hasDeal) return 'warning';
  const map: Record<string, BadgeVariant> = {
    submitted: 'info',
    in_review: 'warning',
    approved:  'success',
    rejected:  'danger',
  };
  return map[status] ?? 'default';
};

// ─── Feed active status ───────────────────────────────────────────────────────
export const feedStatusVariant = (isActive: boolean): BadgeVariant =>
  isActive ? 'success' : 'default';
